<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("achievements") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="numberOfItems"
					:currentPage="page"
				/>
			</template>
			<template slot="actions">
				<j-button
					:to="{ name: 'AchievementCreate' }"
					v-can="'writeAchievementTypes'"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_achievement')"
					:value="searchTerm"
					@input="onSearch"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link
									:to="{
										name: `AchievementDetails`,
										params: { id: item.id }
									}"
									>{{ item.name }}</router-link
								>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="showModal(item)"
									/>
								</div>
							</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="removeAchievementType(selectedItem)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_achievement") }}</j-dialog
		>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="numberOfItems"
				:perPage="itemsPerPage"
				:activePage="page"
				@click="onPageChange($event)"
			/>
		</div>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDialog from "@/components/JDialog/"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDialog,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			achievementTypes: [],
			numberOfItems: 0,
			selectedItem: null,

			// Search term
			searchTerm: null,

			// Pagination
			page: 1,
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "type",
					text: this.$t("type")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			]
		}
	},

	computed: {
		/**
		 * Compute fetch data for data table
		 * This is what we are gonna display
		 */
		computedList() {
			return this.achievementTypes.map(item => ({
				id: item.id,
				name: item.name || "---",
				description: item.description,
				type: item.type,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.page = pageIndex
			this.fetchAchievementTypes()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value = null) {
			this.searchTerm = value === null || value === "" ? null : value.trim()
			this.page = 1

			this.fetchAchievementTypes()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.page = 1
			this.onSearch()
		},

		async fetchAchievementTypes() {
			const query = gql`
				query achievementTypes(
					$where: achievementTypeWhereInput
					$sort: sortInput
					$limit: Int
					$skip: Int
				) {
					achievementTypes(
						where: $where
						sort: $sort
						limit: $limit
						skip: $skip
					) {
						totalCount
						list {
							name
							description
							id
							type
							createdAt
						}
					}
				}
			`
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.page - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			let where = null
			if (this.searchTerm)
				where = {
					name_contains: this.searchTerm
				}

			this.isLoading = true

			const res = await api({
				query,
				variables: {
					where,
					limit,
					skip,
					sort
				}
			})
			this.isLoading = false

			this.achievementTypes = res?.data?.achievementTypes?.list || []
			this.numberOfItems = res?.data?.achievementTypes?.totalCount || 0
		},

		showModal(item) {
			this.selectedItem = item
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItem = null
			this.$modal.hide("j-dialog")
		},

		async removeAchievementType(item) {
			if (!item) return
			/*
			 * Delete achievementType
			 */
			let variables = {
				id: item.id
			}
			let query = gql`
				mutation deleteAchievementType($id: String!) {
					deleteAchievementType(id: $id) {
						status
						errors
					}
				}
			`
			let res = await api({ query, variables })
			if (res?.data?.deleteAchievementType?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				return
			}
			/*
			 * Delete L10n
			 */
			query = gql`
				mutation deleteAllL10n($key: String!) {
					deleteAllL10n(key: $key) {
						errors
						status
					}
				}
			`
			variables = {
				key: `achievements-${item.id}-name`
			}
			res = await api({ query, variables })

			variables = {
				key: `achievements-${item.id}-description`
			}
			res = await api({ query, variables })
			this.hideModal()
			this.fetchAchievementTypes()
		}
	},

	mounted() {
		this.fetchAchievementTypes()
	}
}
</script>
